/**
 * Tool Selection section for the Test Suite Configuration page.
 *
 * Provides a Cloudscape Select dropdown populated from the existing
 * `useGetToolMetadataQuery` RTK Query hook (same data source as the
 * "All Tools" page). Users pick a tool and a revision track (live/beta/test),
 * and the component auto-fetches the revision data, converts the schema
 * client-side, and dispatches the result into page state.
 *
 * @see Requirements 2.1–2.7
 */

import React, { useEffect, useMemo } from 'react';
import {
  Container,
  Header,
  FormField,
  Select,
  SpaceBetween,
  Alert,
  Box,
  ColumnLayout,
  StatusIndicator,
  Spinner,
} from '@amzn/awsui-components-react';
import type { SelectProps } from '@amzn/awsui-components-react';
import {
  useGetToolMetadataQuery,
  useGetToolRevisionByIdQuery,
} from 'src/store/apis/toolApi';
import { ToolMetadata, ToolRevisionData, VersionRevision } from 'src/store/interfaces/Tool.types';
import { ConfigPageAction, SchemaSummary } from './types';
import { convertToSchemaSummary, serializeSchemaSummary, deriveConfigFromSchema } from './schemaConverter';

// ─── Props ─────────────────────────────────────────────────────────────

export interface ToolSelectionSectionProps {
  selectedToolId: string | null;
  selectedRevisionTrack: 'live' | 'beta' | 'test';
  selectedRevision: ToolRevisionData | null;
  schemaSummary: SchemaSummary | null;
  dispatch: React.Dispatch<ConfigPageAction>;
}

// ─── Constants ─────────────────────────────────────────────────────────

const REVISION_TRACK_OPTIONS: SelectProps.Option[] = [
  { label: 'Live', value: 'live' },
  { label: 'Beta', value: 'beta' },
  { label: 'Test', value: 'test' },
];

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Look up the VersionRevision for a given track on a ToolMetadata entry.
 * Returns null if the tool has no revision for that track.
 */
function getRevisionForTrack(
  tool: ToolMetadata,
  track: 'live' | 'beta' | 'test',
): VersionRevision | null {
  const key = `${track}Revision` as keyof ToolMetadata;
  const rev = tool[key] as VersionRevision | undefined;
  if (rev?.version && rev.revision !== null && rev.revision !== undefined) {
    return rev;
  }
  return null;
}

/**
 * Format a revision identifier for display, e.g. "v1.0 rev 3".
 */
function formatRevisionLabel(version: string, revision: number): string {
  return `v${version} rev ${revision}`;
}

// ─── Component ─────────────────────────────────────────────────────────

const ToolSelectionSection: React.FC<ToolSelectionSectionProps> = ({
  selectedToolId,
  selectedRevisionTrack,
  selectedRevision,
  schemaSummary,
  dispatch,
}) => {
  // ── Fetch all tools ────────────────────────────────────────────────

  const {
    data: toolMetadata,
    isLoading: isLoadingTools,
    error: toolsError,
  } = useGetToolMetadataQuery();

  // ── Build tool dropdown options ────────────────────────────────────

  const toolOptions: SelectProps.Options = useMemo(() => {
    if (!toolMetadata) return [];
    return toolMetadata.map((tool: ToolMetadata) => ({
      label: tool.toolId,
      value: tool.toolId,
      description: tool.category ?? undefined,
    }));
  }, [toolMetadata]);

  // ── Determine the selected tool's metadata ─────────────────────────

  const selectedToolMeta = useMemo(() => {
    if (!toolMetadata || !selectedToolId) return null;
    return toolMetadata.find((t) => t.toolId === selectedToolId) ?? null;
  }, [toolMetadata, selectedToolId]);

  // ── Determine the version/revision for the selected track ──────────

  const trackRevision = useMemo(() => {
    if (!selectedToolMeta) return null;
    return getRevisionForTrack(selectedToolMeta, selectedRevisionTrack);
  }, [selectedToolMeta, selectedRevisionTrack]);

  // ── Fetch the full revision data when we have a valid track revision

  const shouldFetchRevision =
    selectedToolId !== null &&
    trackRevision !== null &&
    trackRevision.version !== null &&
    trackRevision.revision !== null;

  const {
    data: revisionData,
    isLoading: isLoadingRevision,
    error: revisionError,
  } = useGetToolRevisionByIdQuery(
    {
      toolId: selectedToolId!,
      version: trackRevision?.version ?? '',
      revision: trackRevision?.revision ?? 0,
    },
    { skip: !shouldFetchRevision },
  );

  // ── When revision data loads, convert schema and dispatch ──────────

  useEffect(() => {
    if (!revisionData) return;

    const summary = convertToSchemaSummary(revisionData);
    const serialized = serializeSchemaSummary(summary);

    dispatch({
      type: 'SET_REVISION_DATA',
      revision: revisionData,
      summary,
      schemaContent: serialized,
    });

    // Derive config from schema and apply immediately
    const derivation = deriveConfigFromSchema(revisionData);
    dispatch({ type: 'APPLY_SCHEMA_DERIVATION', payload: derivation });
  }, [revisionData, dispatch]);

  // ── Event handlers ─────────────────────────────────────────────────

  const handleToolChange = (detail: SelectProps.ChangeDetail) => {
    const toolId = detail.selectedOption.value;
    if (toolId) {
      dispatch({ type: 'SELECT_TOOL', toolId });
    }
  };

  const handleTrackChange = (detail: SelectProps.ChangeDetail) => {
    const track = detail.selectedOption.value as 'live' | 'beta' | 'test';
    if (track) {
      dispatch({ type: 'SET_REVISION_TRACK', track });
    }
  };

  // ── Derived display values ─────────────────────────────────────────

  const selectedToolOption: SelectProps.Option | null = selectedToolId
    ? { label: selectedToolId, value: selectedToolId }
    : null;

  const selectedTrackOption: SelectProps.Option =
    REVISION_TRACK_OPTIONS.find((o) => o.value === selectedRevisionTrack) ??
    REVISION_TRACK_OPTIONS[0];

  const hasNoRevisionForTrack =
    selectedToolMeta !== null && trackRevision === null;

  const parameterCount = schemaSummary
    ? Object.keys(schemaSummary.parameters).length
    : 0;

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Container header={<Header variant="h2">Tool Selection</Header>}>
      <SpaceBetween size="l">
        <ColumnLayout columns={2}>
          <FormField
            label="Tool"
            description="Select a tool from the Prompt Management tool list"
          >
            <Select
              selectedOption={selectedToolOption}
              onChange={({ detail }) => handleToolChange(detail)}
              options={toolOptions}
              placeholder="Choose a tool"
              filteringType="auto"
              loadingText="Loading tools..."
              statusType={isLoadingTools ? 'loading' : toolsError ? 'error' : 'finished'}
              errorText="Failed to load tools"
              empty="No tools available"
            />
          </FormField>

          <FormField
            label="Revision track"
            description="Select which revision track to use"
          >
            <Select
              selectedOption={selectedTrackOption}
              onChange={({ detail }) => handleTrackChange(detail)}
              options={REVISION_TRACK_OPTIONS}
              disabled={!selectedToolId}
            />
          </FormField>
        </ColumnLayout>

        {/* No revision for the selected track */}
        {hasNoRevisionForTrack && (
          <Alert type="info">
            The tool <strong>{selectedToolId}</strong> has no published revision
            for the <strong>{selectedRevisionTrack}</strong> track. Try selecting
            a different revision track.
          </Alert>
        )}

        {/* Loading revision data */}
        {isLoadingRevision && shouldFetchRevision && (
          <Box textAlign="center" padding={{ vertical: 's' }}>
            <Spinner /> Loading revision data...
          </Box>
        )}

        {/* Revision fetch error */}
        {revisionError && (
          <Alert type="error">
            Failed to load revision data. Please try selecting the tool again.
          </Alert>
        )}

        {/* Confirmation: tool name, parameter count, revision identifier */}
        {selectedRevision && schemaSummary && !isLoadingRevision && (
          <Box>
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Tool name</Box>
                <Box>{schemaSummary.tool_name}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Parameters</Box>
                <Box>{parameterCount}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Revision</Box>
                <Box>
                  <StatusIndicator type="success">
                    {formatRevisionLabel(
                      selectedRevision.version,
                      selectedRevision.revision,
                    )}
                  </StatusIndicator>
                </Box>
              </div>
            </ColumnLayout>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default ToolSelectionSection;
